"use client";

import { premiumReportPlanMap } from "@ishvaram/domain-data/report-plans";
import { trackSEOAgentBridgeClicked, trackSEOIntentCaptured } from "@/lib/analytics";
import { safeSessionSet } from "@/lib/browser-storage";
import { scorePurchaseIntent } from "@/lib/purchase-intent-score";
import { getSEOIntentConfigForSource } from "@/lib/seo-intents";

const SOURCE_PATH = "/career-astrology-consultation";
const PLACEMENT = "career_consultation_report_cta";
const REPORT_PROMPT = "Read my 10th house and prepare my Career & Finance report.";
const CAREER_PLAN = premiumReportPlanMap.career_report;

function fallbackHref() {
  const params = new URLSearchParams({
    intent: "career",
    source: SOURCE_PATH,
    prompt: REPORT_PROMPT,
    auto: "1",
  });
  return `/free-kundali/#${params.toString()}`;
}

export default function CareerConsultationReportCTA() {
  function startReportFlow() {
    const config = getSEOIntentConfigForSource(SOURCE_PATH);
    const purchaseIntent = scorePurchaseIntent({
      intent: config.intent,
      sourcePath: SOURCE_PATH,
      primaryOffer: config.primaryOffer,
    });

    try {
      safeSessionSet("seo_entry_intent", config.intent);
      safeSessionSet("seo_entry_path", SOURCE_PATH);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_prompt", REPORT_PROMPT);
      safeSessionSet("seo_entry_auto_send", "1");
      safeSessionSet("seo_entry_purchase_intent_score", String(purchaseIntent.score));
      safeSessionSet("seo_entry_purchase_intent_band", purchaseIntent.band);
      safeSessionSet("seo_entry_purchase_intent_reason", purchaseIntent.reason);
    } catch {
      // The fallback navigation still carries the intent in the hash.
    }

    trackSEOAgentBridgeClicked({
      source_path: SOURCE_PATH,
      intent: config.intent,
      page_category: config.pageCategory,
      offer_type: config.primaryOffer,
      plan_id: config.reportPlanId,
      target: config.target,
      placement: PLACEMENT,
      purchase_intent_score: purchaseIntent.score,
      purchase_intent_band: purchaseIntent.band,
      purchase_intent_reason: purchaseIntent.reason,
    });
    trackSEOIntentCaptured({
      source_path: SOURCE_PATH,
      intent: config.intent,
      page_category: config.pageCategory,
      target: config.target,
      placement: PLACEMENT,
      chip_prompt: true,
    });

    const form = document.getElementById("start-reading");
    if (!form) {
      window.location.href = fallbackHref();
      return;
    }

    window.dispatchEvent(new Event("seo-intent-refresh"));
    form.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <section className="my-10 rounded-2xl border border-amber-200 bg-amber-50/60 p-6 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            Talk free first, then read the full career analysis
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-700">
            The free chat names the planet blocking your career. If you want the full read in
            writing after that, unlock the {CAREER_PLAN.pages}-page Career &amp; Finance report with
            your 10th house, D10/Dasamsa, dasha timeline, transits, wealth yogas, and upay for Rs{" "}
            {CAREER_PLAN.price_inr.toLocaleString("en-IN")}.
          </p>
        </div>
        <button
          type="button"
          onClick={startReportFlow}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-amber-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-800"
        >
          Ask about your career free
        </button>
      </div>
    </section>
  );
}
